import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, Loader2, Sparkles, Send } from 'lucide-react';
import { dbManager } from '../services/db_manager';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { ShareQuoteData } from './QuoteCardModal';
import { fileStorageService } from '../services/fileStorageService';
import { useMention } from '../hooks/useMention';
import MentionDropdown from './MentionDropdown';

interface Props {
    data: ShareQuoteData;
    onClose: () => void;
    onPostSuccess: () => void;
    bookId?: string;
    imageBlob: Blob;
}

const SocialShareModal: React.FC<Props> = ({
    data,
    onClose, onPostSuccess, bookId, imageBlob
}) => {
  const { t } = useTranslation();

    const { user } = useAuth();
    const { friends } = useData();
    const {
        text: opinion,
        setText: setOpinion,
        handleKeyDown,
        handleSelectMention,
        handleCursorChange,
        mentionResults,
        selectedIndex,
        showDropdown
    } = useMention('', friends);

    const [isPosting, setIsPosting] = useState(false);
    const [status, setStatus] = useState<'editing' | 'success' | 'error'>('editing');
    const [errorMessage, setErrorMessage] = useState('');

    // Previews URL
    const imageUrl = useMemo(() => URL.createObjectURL(imageBlob), [imageBlob]);

    const handlePost = async () => {
        if (!user) {
            setErrorMessage('Você precisa estar logado para publicar.');
            setStatus('error');
            return;
        }

        setIsPosting(true);
        setStatus('editing'); // Ensure we are in editing state to render the preview

        try {
            // 1. the Blob is ALREADY high-res! Just use it 
            const file = new File([imageBlob], `post_${Date.now()}.jpg`, { type: imageBlob.type || 'image/jpeg' });
            
            // 2. Upload to R2 in explicit folder
            const mediaUrl = await fileStorageService.uploadToR2(file, 'biblioteca-backup/community-posts-images');

            // 3. Save to D1
            const { error } = await dbManager.execute({
                action: 'upsert',
                table: 'community_posts',
                data: {
                    id: crypto.randomUUID(),
                    user_id: user.id,
                    author_name: user.user_metadata?.display_name || user.email,
                    content: opinion.trim(), // The "caption" of the post
                    media_url: mediaUrl,     // The static snapshot of the card
                    book_title: data.bookTitle,
                    book_id: bookId || null,
                    user_opinion: opinion.trim(),
                    created_at: new Date().toISOString()
                }
            });

            if (error) throw error;
            setStatus('success');
        } catch (error: any) {
            setErrorMessage(error.message || 'Ocorreu um erro, tente novamente mais tarde. Nossa equipe já está trabalhando na correção...');
            setStatus('error');
        } finally {
            setIsPosting(false);
        }
    };

    const handleConfirmSuccess = () => {
        onPostSuccess();
    };

    const handleConfirmError = () => {
        setStatus('editing');
    };

    return ReactDOM.createPortal((
        <div className="fixed inset-0 z-[600] flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="absolute inset-0" onClick={isPosting ? undefined : onClose} />

            <div className="relative w-full max-w-lg bg-[#151B24] border border-white/10 rounded-[28px] overflow-hidden flex flex-col animate-in slide-in-from-bottom-8 duration-300 max-h-[90vh]">
                {/* Header */}
                <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between bg-white/5">
                    <h2 className="text-sm font-medium text-white flex items-center gap-2">
                        <Sparkles size={16} className="text-primary" />
                        {t('social.newPost') || 'Nova Publicação'}
                    </h2>
                    <button
                        onClick={onClose}
                        disabled={isPosting}
                        className="p-2 hover:bg-white/10 rounded-full text-slate-400 hover:text-white transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 overflow-y-auto no-scrollbar space-y-6">

                    {status === 'success' && (
                        <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in zoom-in-95">
                            <div className="w-16 h-16 rounded-full bg-ok/20 text-ok flex items-center justify-center mb-2">
                                <Sparkles size={32} />
                            </div>
                            <h3 className="text-xl font-semibold text-white">{t('social.postSent')}</h3>
                            <p className="text-sm text-slate-400">{t('social.quoteAvailable')}</p>
                            <button
                                onClick={handleConfirmSuccess}
                                className="mt-8 px-8 py-3 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition-all"
                            >
                                Confirmar
                            </button>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in zoom-in-95">
                            <div className="w-16 h-16 rounded-full bg-bad/20 text-bad flex items-center justify-center mb-2">
                                <X size={32} />
                            </div>
                            <h3 className="text-xl font-semibold text-white">{t('social.errorGeneric')}</h3>
                            <p className="text-sm text-slate-400 px-4">{errorMessage}</p>
                            <button
                                onClick={handleConfirmError}
                                className="mt-8 px-8 py-3 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition-all"
                            >
                                Tentar Novamente
                            </button>
                        </div>
                    )}

                    {status === 'editing' && (
                        <>
                            {/* The PERFECT High-Res Generated Screenshot displayed directly */}
                            <div className="w-full flex justify-center items-center">
                                <img
                                    src={imageUrl}
                                    alt="Preview"
                                    className="w-full max-w-[280px] sm:max-w-[320px] object-cover rounded-[16px]"
                                    style={{ aspectRatio: '9 / 16' }}
                                />
                            </div>

                            {/* User Opinion Input */}
                            <div className="space-y-2 relative">
                                <textarea
                                    value={opinion}
                                    onChange={(e) => {
                                        setOpinion(e.target.value);
                                        handleCursorChange(e.target.selectionStart);
                                    }}
                                    onKeyDown={handleKeyDown}
                                    onKeyUp={(e) => handleCursorChange((e.target as HTMLTextAreaElement).selectionStart)}
                                    onMouseUp={(e) => handleCursorChange((e.target as HTMLTextAreaElement).selectionStart)}
                                    placeholder={t("social.writeOpinion")}
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 min-h-[120px] text-sm text-slate-200 placeholder:text-slate-600 outline-none focus:border-primary/80 transition-all resize-none custom-scrollbar"
                                    maxLength={500}
                                />

                                <div className="absolute bottom-3 right-4">
                                    <span className={`text-[10px] font-bold ${opinion.length > 450 ? 'text-warn' : 'text-slate-600'}`}>
                                        {opinion.length} / 500
                                    </span>
                                </div>

                                {/* Mentions Dropdown */}
                                <MentionDropdown
                                    results={mentionResults}
                                    selectedIndex={selectedIndex}
                                    onSelect={handleSelectMention}
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                {status === 'editing' && (
                    <div className="p-6 pt-0">
                        <button
                            onClick={handlePost}
                            disabled={isPosting}
                            className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:hover:scale-100"
                        >
                            {isPosting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                            {isPosting ? (t('social.publishing') || 'Publicando...') : (t('social.publishOnLibritui') || 'Publicar no Libritui')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    ), document.body);
};

export default SocialShareModal;
